import {LOGIN_SESSION_STORAGE_PREFIX} from './loginSessionStorage'

/**
 * The business this browser tab works in (backend 1.0.0.747+, Services/Businesses/TabBusiness). A browser's tabs share
 * one session cookie, and until then the business chosen last in any tab was the business of every tab: a form still
 * open in another tab saved into the newly chosen business. Now each tab keeps its own business (sessionStorage, which
 * a tab never shares) and names it in every request; the server checks it is still one of the user's.
 *
 * The header is sent only after the session check said the server reads it (`bpt`): an older server's CORS refuses an
 * unknown header and every request would fail. The session check itself names the business in its address.
 */

export const TAB_BUSINESS_HEADER = 'X-Business-Id'
export const TAB_BUSINESS_QUERY = 'tabBusiness'
/** `?business=<id>` in a panel address: «باز کردن در تب جدید» opens the new tab in that business. */
export const OPEN_BUSINESS_PARAM = 'business'
export const BUSINESS_NOT_AVAILABLE = 'BUSINESS_NOT_AVAILABLE'
export const OTHER_TAB_SWITCHED_EVENT = 'megapay:business:other-tab'

// Cleared with the sign-in (clearLoginSessionStorage), so the next user of the tab starts from their own.
const TAB_KEY = `${LOGIN_SESSION_STORAGE_PREFIX}tab-business`
// The business of the tab used last in this browser: a new tab opened from it (a middle click) starts there too.
const LAST_KEY = 'mp:last-tab-business'
// A message for after the reload that changed the tab's business («… دیگر در دسترس نیست»).
const NOTICE_KEY = `${LOGIN_SESSION_STORAGE_PREFIX}business-notice`
const CHANNEL_NAME = 'mp:business'
// A refused business reloads the page once; a second refusal within this window is left to the page.
const RELOAD_GUARD_KEY = `${LOGIN_SESSION_STORAGE_PREFIX}business-reload-at`
const RELOAD_GUARD_MS = 15000

type StoredBusiness = {u?: number; b: number}

export type TabBusinessUser = {id?: number; cpi?: number; bpt?: boolean; support?: unknown}

export type OtherTabSwitch = {from?: number; to: number; name?: string; userId?: number}

let serverReadsHeader = false
let openedFromLink: number | undefined
let linkChecked = false

const readJson = (storage: Storage | undefined, key: string): StoredBusiness | undefined => {
  try {
    const value = storage?.getItem(key)
    if (!value) return undefined
    const parsed = JSON.parse(value)
    return parsed && Number.isInteger(parsed.b) && parsed.b >= 0
      ? {u: Number.isInteger(parsed.u) ? parsed.u : undefined, b: parsed.b}
      : undefined
  } catch {
    return undefined
  }
}

const writeJson = (storage: Storage | undefined, key: string, value: StoredBusiness) => {
  try {
    storage?.setItem(key, JSON.stringify(value))
  } catch {
    // Without storage (a private window that refuses it) the tab follows the session's business, as before.
  }
}

const session = () => (typeof sessionStorage === 'undefined' ? undefined : sessionStorage)
const local = () => (typeof localStorage === 'undefined' ? undefined : localStorage)

const parseId = (value: string | null | undefined) => {
  if (!value || !/^\d{1,9}$/.test(value.trim())) return undefined
  return Number(value.trim())
}

/** Takes `?business=<id>` out of the address once (the page keeps its own address otherwise). */
const consumeOpenedFromLink = () => {
  if (linkChecked || typeof window === 'undefined') return openedFromLink
  linkChecked = true
  try {
    const url = new URL(window.location.href)
    const id = parseId(url.searchParams.get(OPEN_BUSINESS_PARAM))
    if (id === undefined) return undefined
    openedFromLink = id
    url.searchParams.delete(OPEN_BUSINESS_PARAM)
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  } catch {
    openedFromLink = undefined
  }
  return openedFromLink
}

/** The business opened by a link, until the session check has answered (then the tab keeps what it got). */
export const businessOpenedFromLink = () => consumeOpenedFromLink()

/**
 * The business this tab asks the session check for: one opened by a link, else the tab's own, else the business of the
 * tab used last (a tab opened from another one). The server falls back to the session's business when it is not the
 * user's, so a stale value costs nothing.
 */
export const requestedTabBusiness = (): number | undefined => {
  const fromLink = consumeOpenedFromLink()
  if (fromLink !== undefined) return fromLink
  return readJson(session(), TAB_KEY)?.b ?? readJson(local(), LAST_KEY)?.b
}

/** The tab's business as the session check answered it; a server that does not read the header keeps the old ways. */
export const rememberTabBusiness = (user?: TabBusinessUser) => {
  serverReadsHeader = Boolean(user?.bpt) && !user?.support
  openedFromLink = undefined
  if (!user || !serverReadsHeader || typeof user.cpi !== 'number') {
    forgetTabBusiness()
    return
  }
  const stored = {u: user.id, b: user.cpi}
  writeJson(session(), TAB_KEY, stored)
  writeJson(local(), LAST_KEY, stored)
}

/** The tab became the one in use: a tab opened from it now starts in its business. */
export const markTabInUse = () => {
  const stored = readJson(session(), TAB_KEY)
  if (stored && serverReadsHeader) writeJson(local(), LAST_KEY, stored)
}

export const forgetTabBusiness = () => {
  try {
    session()?.removeItem(TAB_KEY)
  } catch {
    // Nothing kept.
  }
}

export const tabBusinessId = () => (serverReadsHeader ? readJson(session(), TAB_KEY)?.b : undefined)

/** The header for one API request, when the server reads it and the tab has a business. */
export const tabBusinessHeaders = (): Record<string, string> | undefined => {
  const id = tabBusinessId()
  return id === undefined ? undefined : {[TAB_BUSINESS_HEADER]: String(id)}
}

export const serverKeepsBusinessPerTab = () => serverReadsHeader

/** For tests: back to a fresh page. */
export const resetTabBusinessState = () => {
  serverReadsHeader = false
  openedFromLink = undefined
  linkChecked = false
}

export const setBusinessNotice = (message: string) => {
  try {
    session()?.setItem(NOTICE_KEY, message)
  } catch {
    // The reload happens without the message.
  }
}

/** The message left for after a reload, read once. */
export const takeBusinessNotice = (): string | undefined => {
  try {
    const message = session()?.getItem(NOTICE_KEY) || undefined
    session()?.removeItem(NOTICE_KEY)
    return message
  } catch {
    return undefined
  }
}

/**
 * The server refused the tab's business (403 BUSINESS_NOT_AVAILABLE: the user was taken off it, or it was switched
 * off): the tab drops it and reloads in the session's business. Only once within a few seconds, so a server that keeps
 * refusing cannot reload the page in a loop.
 */
export const leaveUnavailableBusiness = (message?: string) => {
  forgetTabBusiness()
  try {
    const now = Date.now()
    const last = Number(session()?.getItem(RELOAD_GUARD_KEY) || 0)
    if (now - last < RELOAD_GUARD_MS) return false
    session()?.setItem(RELOAD_GUARD_KEY, String(now))
  } catch {
    return false
  }
  setBusinessNotice(
    message?.trim() || 'این کسب‌وکار دیگر در فهرست کسب‌وکارهای شما نیست؛ کسب‌وکار دیگری باز شد.'
  )
  window.location.reload()
  return true
}

let channel: BroadcastChannel | null | undefined

const getChannel = () => {
  if (channel === undefined) {
    channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME)
    channel?.addEventListener('message', (event: MessageEvent) => {
      const data = event.data as OtherTabSwitch | undefined
      if (data && Number.isInteger(data.to)) {
        window.dispatchEvent(
          new CustomEvent<OtherTabSwitch>(OTHER_TAB_SWITCHED_EVENT, {detail: data})
        )
      }
    })
  }
  return channel
}

/** Starts hearing the other tabs (BusinessTabNotices). */
export const listenToOtherTabs = () => {
  getChannel()
}

/** Tells the other tabs of this browser that this one opened another business; they keep their own. */
export const announceBusinessSwitch = (change: OtherTabSwitch) => {
  try {
    getChannel()?.postMessage(change)
  } catch {
    // The other tabs simply hear nothing.
  }
}
